import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@bso/shared";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Requests session from server. Scoped to MODULE intentionally: defined inside
 * `beforeLoad`, `unicorn/consistent-function-scoping` flags as ERROR
 * in `pnpm lint` (with `tsc --noEmit` passing).
 */
const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });

type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Writes server response into session machine and cache.
 *
 * ALWAYS runs when response arrives, even if timeout elapsed
 * and login rendered: request not cancelled. Syncing here
 * prevents BootManager seeing pending or stale session during
 * route transition, which causes redirect loops.
 *
 * Module scoped for same reason as `fetchSession`.
 */
function syncReply(reply: FetchResult): FetchResult {
  const { data: replyData, error: replyError } = reply;
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
}

/**
 * Bible Strong auth wrapper.
 *
 * NOTE (#13 — de-clone): Bible Strong deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass `AuthCard` shell every clone
 * inherited and which therefore read as a clone). Instead each screen owns its
 * own `AuthPage` — a single bound "reading page" surface where the serif
 * masthead and the form live together on one sheet. So this route is a thin
 * wrapper: it only owns the "already authenticated → redirect" gate and the page
 * transition. The background, masthead and scroll container live in `AuthPage`,
 * rendered per-screen.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here, otherwise the switcher's "Add account"
    // would bounce straight back to the app and you could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` must NOT be unbounded. A STALLED
    // BRAKED (held, neither 401 nor 5xx: both resolve quickly) left
    // `beforeLoad` unresolved, router without mounting the route and entire app IN
    // WHITE — measured on `/login` over `main`: `body.innerText` of 11 chars at
    // 3 s (only `#app-loader` in `index.html`) and 0 chars at
    // 6/10/15/20 s, with empty `#root`. Sister `_app` guard already ran
    // with cap; this lacked it because value was LOCAL literal,
    // which is why I1 closed green measuring `/` rather than `/login`.
    //
    // The request is NOT canceled: it stays alive and synchronizes machine + cache when
    // arrives (`syncReply`). If session exists, BootManager moves
    // user away from login; only pays a flicker, never a
    // blank screen.
    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Timeout or network error: render the login page. The late response remains
      // attached above, and BootManager redirects if it contains a session.
      return;
    }

    const { data } = result;

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
    // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
    const sessionData = data as { user?: unknown } | null | undefined;
    if (sessionData?.user) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
